import React from "react";
import {
  TextField,
  MenuItem,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
  makeStyles,
} from "@material-ui/core";
import { useFormikContext } from "formik";
// import { FIELD_TYPES } from "../config/formSchema";

const useStyles = makeStyles((theme) => ({
  fieldRoot: {
    width: "100%",
  },
  radioLabel: {
    fontSize: "0.8125rem",
    fontWeight: 500,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
    letterSpacing: "0.02em",
    textTransform: "uppercase",
  },
  radioGroup: {
    flexDirection: "row",
    gap: theme.spacing(2),
  },
  radioControlLabel: {
    "& .MuiFormControlLabel-label": {
      fontSize: "0.9375rem",
    },
  },
  radio: {
    color: theme.palette.text.secondary,
    "&.Mui-checked": {
      color: theme.palette.primary.main,
    },
  },
  helperText: {
    fontSize: "0.75rem",
    marginTop: theme.spacing(0.5),
  },
  textField: {
    "& .MuiInputBase-root": {
      fontSize: "0.9375rem",
      borderRadius: 0,
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.9375rem",
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.palette.divider,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.text.secondary,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        borderWidth: 1,
      },
    },
    "& textarea": {
      lineHeight: 1.65,
    },
  },
  select: {
    "& .MuiSelect-root": {
      fontSize: "0.9375rem",
    },
  },
  menuItem: {
    fontSize: "0.9375rem",
  },
}));

const FormField = ({ field, FIELD_TYPES }) => {
  const classes = useStyles();
  const { values, errors, touched, handleChange, handleBlur } = useFormikContext();

  const fieldError = touched[field.id] && errors[field.id];

  // Conditional rendering — hide field if dependency not met
  if (field.dependsOn) {
    const { field: depField, value: depValue } = field.dependsOn;
    if (values[depField] !== depValue) return null;
  }

  const commonProps = {
    id: field.id,
    name: field.id,
    label: field.label + (field.required ? " *" : ""),
    value: values[field.id] || "",
    onChange: handleChange,
    onBlur: handleBlur,
    error: Boolean(fieldError),
    helperText: fieldError || field.helperText || "",
    variant: "outlined",
    fullWidth: true,
    className: `${classes.textField} ${classes.fieldRoot}`,
    size: "medium",
    FormHelperTextProps: { className: classes.helperText },
  };

  switch (field.type) {
    case FIELD_TYPES.TEXTAREA: {
      // With a maxLength, show a live counter and flag the field as soon as it runs over.
      const length = (values[field.id] || "").length;
      const overLimit = Boolean(field.maxLength) && length > field.maxLength;
      const counterProps = field.maxLength
        ? {
            error: Boolean(fieldError) || overLimit,
            helperText:
              fieldError ||
              `${length}/${field.maxLength} characters${
                field.helperText ? ` · ${field.helperText}` : ""
              }`,
          }
        : {};
      return (
        <TextField
          {...commonProps}
          {...counterProps}
          multiline
          rows={field.rows || 4}
          placeholder={field.placeholder}
        />
      );
    }

    case FIELD_TYPES.SELECT:
      return (
        <TextField
          {...commonProps}
          select
          className={`${classes.textField} ${classes.select} ${classes.fieldRoot}`}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: Boolean(values[field.id]) || undefined }}
        >
          {(field.options || []).map((opt) => (
            <MenuItem key={opt.value} value={opt.value} className={classes.menuItem}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      );

    case FIELD_TYPES.RADIO:
      return (
        <FormControl
          component="fieldset"
          error={Boolean(fieldError)}
          className={classes.fieldRoot}
        >
          <FormLabel component="legend" className={classes.radioLabel}>
            {field.label}
            {field.required ? " *" : ""}
          </FormLabel>
          <RadioGroup
            name={field.id}
            value={values[field.id] || ""}
            onChange={handleChange}
            className={classes.radioGroup}
          >
            {(field.options || []).map((opt) => (
              <FormControlLabel
                key={opt.value}
                value={opt.value}
                label={opt.label}
                className={classes.radioControlLabel}
                control={<Radio className={classes.radio} size="small" />}
              />
            ))}
          </RadioGroup>
          {fieldError && (
            <FormHelperText className={classes.helperText}>{fieldError}</FormHelperText>
          )}
        </FormControl>
      );

    default:
      return (
        <TextField
          {...commonProps}
          type={field.type}
          placeholder={field.placeholder}
        />
      );
  }
};

export default React.memo(FormField);
